import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef } from "react";
const GsbNavigationContext = createContext(null);
export function GsbNavigationProvider({ navigation, location, children, }) {
    const previousLocation = useRef({
        pathname: location.pathname,
        search: location.search,
    });
    const navigationFocusOrigin = useRef(null);
    useEffect(() => {
        const pathChanged = previousLocation.current.pathname !== location.pathname;
        const searchChanged = previousLocation.current.search !== location.search;
        previousLocation.current = {
            pathname: location.pathname,
            search: location.search,
        };
        const origin = navigationFocusOrigin.current;
        // Explicit navigation to a query destination (for example Trash) needs
        // landing focus. Ordinary query-bound filters must retain their input focus.
        if (!pathChanged && (!searchChanged || origin === null))
            return;
        navigationFocusOrigin.current = null;
        const restoreFocus = () => {
            if (document.activeElement !== document.body &&
                document.activeElement !== origin) {
                observer.disconnect();
                return;
            }
            const heading = document.querySelector("main h1");
            if (!heading || heading.getClientRects().length === 0)
                return;
            heading.setAttribute("tabindex", "-1");
            heading.focus({ preventScroll: true });
            observer.disconnect();
        };
        const observer = new MutationObserver(restoreFocus);
        const stopWaiting = () => observer.disconnect();
        const frame = window.requestAnimationFrame(() => {
            observer.observe(document.body, { childList: true, subtree: true });
            document.addEventListener("focusin", stopWaiting, { once: true });
            restoreFocus();
        });
        return () => {
            window.cancelAnimationFrame(frame);
            observer.disconnect();
            document.removeEventListener("focusin", stopWaiting);
        };
    }, [location.pathname, location.search]);
    return (_jsx(GsbNavigationContext.Provider, { value: {
            ...navigation,
            location,
            prepareNavigationFocus: (origin) => {
                navigationFocusOrigin.current = origin;
            },
        }, children: children }));
}
export function useGsbNavigation() {
    const context = useContext(GsbNavigationContext);
    if (context === null) {
        throw new Error("useGsbNavigation requires a GsbNavigationProvider");
    }
    return context;
}
export function useOptionalGsbNavigation() {
    return useContext(GsbNavigationContext);
}
/** Protect route transitions and document unloads while an editor is dirty. */
export function useGsbNavigationGuard(dirty, confirmLeave) {
    const navigation = useOptionalGsbNavigation();
    const block = navigation?.block;
    const confirmRef = useRef(confirmLeave);
    useEffect(() => {
        confirmRef.current = confirmLeave;
    }, [confirmLeave]);
    useEffect(() => {
        if (!dirty)
            return;
        const unregister = block?.(() => confirmRef.current());
        const preventUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", preventUnload);
        return () => {
            unregister?.();
            window.removeEventListener("beforeunload", preventUnload);
        };
    }, [block, dirty]);
    return block !== undefined;
}
//# sourceMappingURL=navigation.js.map